import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"

import { getAdjustedOnboardingStep } from "../../../../../Utils/onboarding/getAdjustedOnboardingStep.js"
import { getJourneyState, JOURNEY_FLOWS } from "../../../../../Utils/onboarding/getJourneyState.js"
import { getOnboardingAccess } from "../../../../../Utils/onboarding/getOnboardingAccess.js"
import {
  CHOICE_FLOWS,
  hasOnboardingProgress,
  resolveProductChoice
} from "../../../../../Utils/onboarding/productChoice.js"
import {
  FEATURES_STEP_KEY,
  getSkippedStepKeys
} from "../../../../../Utils/onboarding/stepSequence.js"
import { createAccountStepPath, FEATURES_STEP_PATH } from "../../../../../Utils/utils.jsx"
import { BA_STEPS } from "../../../utils/businessSteps.js"
import { FEATURES_CHECKLIST_ITEM } from "../../../utils/onboardingSteps.js"

const useBusinessStepsBlock = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const new_onboarding_finished = useSelector(
    (state) => state.auth.userInfo.new_onboarding_finished
  )

  const userInfo = useSelector((state) => state.auth.userInfo)
  const userState = useSelector((state) => state.user)
  const businessAccountsFetching = useSelector((state) => state.user.businessAccountsFetching)
  const maxCreateStepsFetching = useSelector((state) => state.user.maxCreateStepsFetching)
  // Both lookups shape the row set (which business is unfinished, how many steps it has); Continue
  // waits for both so the merchant never acts on a list that is about to change (OB-3-17).
  const stepsLoading = businessAccountsFetching || maxCreateStepsFetching
  const journey = getJourneyState({
    userInfo,
    userState,
    pathname
  })
  const { isStepCompleted, isStepCurrent } = getOnboardingAccess({
    nextCreateAccountStep: getAdjustedOnboardingStep(journey.currentStep, new_onboarding_finished),
    isInvite: journey.flow === JOURNEY_FLOWS.INVITED_CREATE_BUSINESS
  })

  // This business's own choice; a business that already has its company profile saved but no
  // choice recorded started before the step existed and reads as Prevent only (OB-X-07).
  const flow = CHOICE_FLOWS.BUSINESS
  const hasProgress = hasOnboardingProgress({ flow, nextCreateAccountStep: journey.currentStep })
  const choice = resolveProductChoice({ flow, hasProgress })
  const featuresDone = choice !== null
  const skippedIds = getSkippedStepKeys(choice).map((key) => BA_STEPS[key])

  // The journey already dropped the billing steps this user account no longer needs; the choice
  // drops the Prevent-only ones on top (OB-X-01).
  const backendSteps = journey.steps.filter((s) => !skippedIds.includes(s.step))

  // The pointer may sit on a step the choice skipped; the merchant continues with the first one
  // that still applies from there on.
  const currentStep = backendSteps.find(
    (s) =>
      !isStepCompleted(getAdjustedOnboardingStep(s.step, new_onboarding_finished)) &&
      (isStepCurrent(getAdjustedOnboardingStep(s.step, new_onboarding_finished)) ||
        s.step > journey.currentStep)
  )

  const featuresItem = {
    ...FEATURES_CHECKLIST_ITEM,
    isCompleted: featuresDone,
    isContinue: !featuresDone
  }

  const withState = backendSteps.map((s) => ({
    ...s,
    isCompleted: isStepCompleted(getAdjustedOnboardingStep(s.step, new_onboarding_finished)),
    isContinue: featuresDone && s.step === currentStep?.step
  }))

  const preparedSteps = [featuresItem, ...withState].map((s, i) => ({ ...s, id: i + 1 }))
  const completedBusinessStep = preparedSteps.filter((s) => s.isCompleted).length

  const handleContinue = (step) => {
    if (step === FEATURES_STEP_KEY) {
      navigate(FEATURES_STEP_PATH.business)

      return
    }

    navigate(createAccountStepPath[step])
  }

  return {
    preparedSteps,
    handleContinue,
    completedBusinessStep,
    stepsLoading
  }
}

export default useBusinessStepsBlock
